import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AuthProvider, getDefaultRouteForUser, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Issues = lazy(() => import('./pages/admin/Issues'));
const IssueDetail = lazy(() => import('./pages/admin/IssueDetail'));
const Projects = lazy(() => import('./pages/admin/Projects'));
const ProjectDetail = lazy(() => import('./pages/admin/ProjectDetail'));
const Members = lazy(() => import('./pages/admin/Members'));
const WorkflowEditor = lazy(() => import('./pages/admin/WorkflowEditor'));
const Learn = lazy(() => import('./pages/admin/Learn'));

const LoadingFallback = () => (
  <div className="ui-dark-page flex min-h-screen items-center justify-center">
    <div className="text-center">
      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-white/65">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const AdminOnlyRoute = () => {
  const { isAdmin, user } = useAuth();

  if (!isAdmin) {
    return <Navigate to={getDefaultRouteForUser(user)} replace />;
  }

  return <Outlet />;
};

const AppRoutes = () => {
  const { isAuthenticated, isBootstrapping, user } = useAuth();

  if (isBootstrapping) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to={getDefaultRouteForUser(user)} replace />
            ) : (
              <LandingPage />
            )
          }
        />

        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Navigate to={getDefaultRouteForUser(user)} replace />} />
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/developer/dashboard" element={<Dashboard />} />
          <Route path="/business-analyst/dashboard" element={<Dashboard />} />
          <Route path="/member/dashboard" element={<Dashboard />} />

          <Route path="/issues" element={<Issues />} />
          <Route path="/admin/issues" element={<Issues />} />
          <Route path="/developer/issues" element={<Issues />} />
          <Route path="/business-analyst/issues" element={<Issues />} />
          <Route path="/member/issues" element={<Issues />} />

          <Route path="/issue/:id" element={<IssueDetail />} />
          <Route path="/issues/:id" element={<IssueDetail />} />
          <Route path="/admin/issue/:id" element={<IssueDetail />} />
          <Route path="/developer/issue/:id" element={<IssueDetail />} />
          <Route path="/business-analyst/issue/:id" element={<IssueDetail />} />
          <Route path="/member/issue/:id" element={<IssueDetail />} />

          <Route path="/projects" element={<Projects />} />
          <Route path="/admin/projects" element={<Projects />} />
          <Route path="/developer/projects" element={<Projects />} />
          <Route path="/business-analyst/projects" element={<Projects />} />
          <Route path="/member/projects" element={<Projects />} />

          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/admin/projects/:id" element={<ProjectDetail />} />
          <Route path="/developer/projects/:id" element={<ProjectDetail />} />
          <Route path="/business-analyst/projects/:id" element={<ProjectDetail />} />
          <Route path="/member/projects/:id" element={<ProjectDetail />} />

          <Route path="/learn" element={<Learn />} />
          <Route path="/admin/learn" element={<Learn />} />
          <Route path="/developer/learn" element={<Learn />} />
          <Route path="/business-analyst/learn" element={<Learn />} />
          <Route path="/member/learn" element={<Learn />} />

          <Route element={<AdminOnlyRoute />}>
            <Route path="/members" element={<Members />} />
            <Route path="/admin/members" element={<Members />} />
            <Route path="/workflows" element={<WorkflowEditor />} />
            <Route path="/admin/workflows" element={<WorkflowEditor />} />
          </Route>
        </Route>

        <Route
          path="/admin"
          element={<Navigate to={isAuthenticated ? getDefaultRouteForUser(user) : '/'} replace />}
        />
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? getDefaultRouteForUser(user) : '/'} replace />}
        />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
            <Router>
              <AppRoutes />
            </Router>
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
